import { seo } from '#utils/seo';

const SITE_URL = 'https://teamplay.gg';

const SOCIAL_LINKS = [
  'https://discord.gg/gt56cQ4nWb',
  'https://x.com/teamplay_gg',
  'https://www.reddit.com/r/teamplay',
];

const SITE_NAME = 'Teamplay.gg';
const TITLE_MAX = 60;
const DESC_MAX = 160;

const getLocalePath = (pathname: string, locale: 'en' | 'ru') =>
  locale === 'ru' ? `/ru${pathname}` : pathname;

/** Collapse whitespace/newlines (admin textareas leak double spaces) and trim. */
const clean = (value: string | null | undefined): string | undefined =>
  value?.replaceAll(/\s+/g, ' ').trim() || undefined;

/** Trim to `max` chars on a word boundary, adding an ellipsis when cut. */
const clamp = (value: string, max: number): string => {
  if (value.length <= max) return value;
  const cut = value.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

// Locale-aware keyword templates. Used only when the admin field is empty - the
// admin value (whatever locale it's in) always wins. This is what fills the RU
// meta on /ru pages whose RU admin fields are blank (the API returns them empty).
const TITLE_TEMPLATES = {
  en: {
    index: `${SITE_NAME} - Free LFG Platform to Find Teammates`,
    full: (g: string) => `${g} LFG & Team Finder - Find Teammates | ${SITE_NAME}`,
    compact: (g: string) => `${g} LFG & Team Finder | ${SITE_NAME}`,
    short: (g: string) => `${g} LFG | ${SITE_NAME}`,
  },
  // RU-формулы построены по живым запросам Яндекса (август 2026), а не по кальке с EN.
  // Головной кластер — «поиск тиммейтов»: 551 показ/мес на главную при позиции 11 и CTR 3.3%,
  // то есть запрос должен стоять ПЕРВЫМ, а не после аббревиатуры LFG. Отдельно добавлен
  // бренд кириллицей «Тимплей» — 185 показов на позиции 4.9 при почти нулевом CTR.
  ru: {
    index: `Поиск тиммейтов для игр – Тимплей | ${SITE_NAME}`,
    full: (g: string) => `Поиск тиммейтов в ${g} – собрать пати | ${SITE_NAME}`,
    compact: (g: string) => `Поиск тиммейтов в ${g} | ${SITE_NAME}`,
    short: (g: string) => `${g}: поиск тиммейтов`,
  },
};

const DESC_TEMPLATES = {
  en: {
    index:
      'Teamplay is the free LFG platform & team finder: find teammates by game, rank, role and region, then talk in built-in voice. Squad up in minutes.',
    game: (g: string) =>
      `Find ${g} teammates by rank, role and region on Teamplay - free LFG with built-in voice chat. Browse live players and squad up fast.`,
  },
  // Описания намеренно расходятся с EN-формулой: RU-аудитория ищет естественным языком
  // («ищу тиммейта в фортнайт», «ищу людей в овервотч» — сотни показов в месяц), поэтому
  // описание начинается с её же вопроса. Словарь строго игровой: «пати», «кто сейчас в
  // поиске» — то, как игроки говорят в чате; канцелярит вроде «заявок» читается как чужой
  // текст, а «бесплатно» ставит нас в один ряд с агрегаторами вместо разговора о ценности.
  ru: {
    index:
      'Тимплей – поиск тиммейтов для игр по рангу, роли и региону. Смотри, кто сейчас в поиске, собери пати и созвонись в голосовом чате прямо на сайте.',
    game: (g: string) =>
      `Ищешь тиммейтов в ${g}? Смотри, кто сейчас в поиске, или оставь свой пост – ранг, роль, регион. Голосовой чат на сайте, созвон в один клик.`,
  },
};

/**
 * SEO title: the admin value wins (editorial), otherwise a locale-aware keyword
 * template that always contains "<Game> LFG". Length-safe for long game names.
 */
// The API stores title/description as a single EN-only field (verified: the
// Language header does not localize them), so the admin value is only trusted on
// EN pages - RU pages always use the RU keyword template. If the backend ever
// serves localized RU admin copy, drop the `locale === 'en'` guard here.
const adminFor = (raw: string | null | undefined, locale: 'en' | 'ru') =>
  locale === 'en' ? clean(raw) : undefined;

const buildTitle = (
  raw: string | null | undefined,
  gameName: string | null | undefined,
  locale: 'en' | 'ru'
): string => {
  const admin = adminFor(raw, locale);
  if (admin) return admin;
  const t = TITLE_TEMPLATES[locale];
  if (!gameName) return t.index;
  const full = t.full(gameName);
  if (full.length <= TITLE_MAX) return full;
  const compact = t.compact(gameName);
  // `short` still overflows for very long names, so clamp as a last resort.
  return compact.length <= TITLE_MAX ? compact : clamp(t.short(gameName), TITLE_MAX);
};

/** SEO description: admin value (EN pages) or a locale-aware fallback, clamped to 160. */
const buildDescription = (
  raw: string | null | undefined,
  gameName: string | null | undefined,
  locale: 'en' | 'ru'
): string => {
  const admin = adminFor(raw, locale);
  const d = DESC_TEMPLATES[locale];
  const base = admin ?? (gameName ? d.game(gameName) : d.index);
  return clamp(base, DESC_MAX);
};

type LandingHeadOptions = {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  image?: string | null;
  /** '' or 'index' for the homepage, otherwise the game slug */
  gameSlug: string;
  /** Human game name for breadcrumbs (empty on the homepage) */
  gameName?: string | null;
  locale?: string;
  /** Emit robots noindex (e.g. for preview routes) */
  noindex?: boolean;
};

/**
 * Builds head() output for landing pages: SEO meta (via seo()), canonical +
 * hreflang alternates, and schema.org JSON-LD (Organization + WebSite always,
 * BreadcrumbList on game pages). FAQPage JSON-LD is emitted by the FAQ component.
 */
export const landingHead = ({
  title,
  description,
  keywords,
  image,
  gameSlug,
  gameName,
  locale,
  noindex = false,
}: LandingHeadOptions) => {
  const isIndex = !gameSlug || gameSlug === 'index';
  const path = isIndex ? '' : `/${gameSlug}`;
  const activeLocale = locale === 'ru' ? 'ru' : 'en';
  // en homepage → https://teamplay.gg ; ru homepage → https://teamplay.gg/ru
  // en game     → https://teamplay.gg/<slug> ; ru game → https://teamplay.gg/ru/<slug>
  const canonicalUrl = `${SITE_URL}${getLocalePath(path, activeLocale)}` || SITE_URL;

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Teamplay',
    url: SITE_URL,
    logo: `${SITE_URL}/android-chrome-512x512.png`,
    sameAs: SOCIAL_LINKS,
  };

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Teamplay.gg',
    url: SITE_URL,
    description:
      'Free LFG (Looking for Group) platform that helps gamers find teammates by game, rank, platform and schedule.',
  };

  const breadcrumb =
    !isIndex && gameName
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Teamplay', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: gameName, item: canonicalUrl },
          ],
        }
      : null;

  const jsonLd = [organization, website, ...(breadcrumb ? [breadcrumb] : [])];

  const links = [
    { rel: 'canonical', href: canonicalUrl },
    { rel: 'alternate', hrefLang: 'en', href: `${SITE_URL}${getLocalePath(path, 'en')}` },
    { rel: 'alternate', hrefLang: 'ru', href: `${SITE_URL}${getLocalePath(path, 'ru')}` },
    { rel: 'alternate', hrefLang: 'x-default', href: `${SITE_URL}${getLocalePath(path, 'en')}` },
  ];

  // Guarantee a keyword-rich, locale-correct title/description for every game even
  // when the admin fields are empty (cs2/pubg, or any blank RU field), and normalize
  // whatever the admin did provide.
  // On the homepage the API still returns a generic name ("online gaming"), so
  // force the index template instead of treating it as a game.
  const templateGame = isIndex ? null : gameName;
  const finalTitle = buildTitle(title, templateGame, activeLocale);
  const finalDescription = buildDescription(description, templateGame, activeLocale);
  // The "[Preview]" tab label is cosmetic and only shown on the noindex preview
  // routes; it drops away automatically once these pages ship without noindex.
  const displayTitle = noindex ? `[Preview] ${finalTitle}` : finalTitle;
  // Fall back to the site social banner so every page has an og:image for sharing.
  const ogImage = clean(image) ?? `${SITE_URL}/preview.png`;

  const meta = [
    ...seo({
      title: displayTitle,
      description: finalDescription,
      keywords: clean(keywords),
      image: ogImage,
      url: canonicalUrl,
    }),
    ...(noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
  ];

  return {
    meta,
    links,
    scripts: jsonLd.map((schema) => ({
      type: 'application/ld+json',
      children: JSON.stringify(schema),
    })),
  };
};
